import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHasAnyRole } from '@/hooks/useSession';
import { useDebounce } from '@/hooks/useDebounce';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import { UrgentStrip } from '../components/UrgentStrip';
import { AccountPortfolioTable } from '../components/AccountPortfolioTable';
import { AmFilterDropdown } from '../components/AmFilterDropdown';
import { MetricTooltip } from '../components/MetricTooltip';
import { AmSummaryTable } from '../components/AmSummaryTable';
import { fetchAmDashboard } from '../api';
import {
  amDashboardKeys,
  useAmDashboard,
  useAmDashboardAccountNames,
} from '../hooks/useAmDashboard';
import type { AmDashboardAccount, IssueFilter } from '../types';

const COLUMN_FILTERS_STORAGE_KEY = 'atek-am-dashboard-column-filters';
const HISTORY_WINDOW_HOURS = 24;
const PAGE_SUMMARY_TOOLTIP =
  'These totals reflect the entire portfolio of the selected sales manager. They are not affected by table search, status, tier, issue filters, sorting, or pagination. Each card tooltip explains the exact PostgreSQL calculation.';
const URGENT_ISSUES_TOOLTIP =
  'These issue counts reflect the entire portfolio of the selected sales manager. Clicking a card filters the table below without changing these totals. Each card tooltip explains the exact PostgreSQL calculation.';

const SUMMARY_CARD_TOOLTIPS = {
  activeTransmitters:
    'Counts synced PostgreSQL transmitters linked to accounts in scope where activated is true and the last packet was received within the last 2 hours. Internal and excluded organisations are not counted.',
  alertsOpened:
    'Counts PostgreSQL alert history rows linked to accounts in scope where openedAt is within the last 24 hours, based on the latest account health snapshot.',
  emailsSent:
    'Counts PostgreSQL email history rows linked to accounts in scope where sendAt is within the last 24 hours, based on the latest account health snapshot.',
  callsSent:
    'Counts PostgreSQL call history rows linked to accounts in scope where createdAt is within the last 24 hours, based on the latest account health snapshot.',
  expiringContracts:
    'Counts accounts in scope whose latest account health snapshot shows daysUntilSubscriptionExpiry between 0 and 90 days, inclusive.',
} as const;

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(COLUMN_FILTERS_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

interface SummaryCardProps {
  title: string;
  tooltipText: string;
  count?: number | null;
  accountsAffected?: number | null;
  windowHours: number;
  helperText?: string;
  accentBorderClassName: string;
  valueClassName: string;
  isLoading?: boolean;
  isError?: boolean;
}

function SummaryCard({
  title,
  tooltipText,
  count,
  accountsAffected,
  windowHours,
  helperText,
  accentBorderClassName,
  valueClassName,
  isLoading = false,
  isError = false,
}: SummaryCardProps) {
  const displayCount = isLoading
    ? '...'
    : isError || typeof count !== 'number'
      ? '--'
      : count.toLocaleString();

  let resolvedHelperText = helperText ?? `Loading last ${windowHours} hours`;
  if (isError) {
    resolvedHelperText = 'Unable to load this metric right now';
  } else if (!helperText && typeof accountsAffected === 'number') {
    resolvedHelperText = `Across ${accountsAffected.toLocaleString()} account${
      accountsAffected !== 1 ? 's' : ''
    } in the last ${windowHours} hours`;
  }

  return (
    <div className={`rounded-lg border border-slate-200 border-l-4 bg-white p-4 shadow-sm ${accentBorderClassName}`}>
      <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span>{title}</span>
        <MetricTooltip label={`Explain ${title} calculation`} text={tooltipText} />
      </div>
      <div className={`mt-1 text-3xl font-bold ${valueClassName}`}>{displayCount}</div>
      <div className="text-xs text-muted-foreground">{resolvedHelperText}</div>
    </div>
  );
}

export function AmDashboardPage() {
  const canViewAllAms = useHasAnyRole(['full_admin', 'director', 'csm']);
  const queryClient = useQueryClient();

  // Filter state
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [tier, setTier] = useState<string | undefined>();
  const [issueFilter, setIssueFilter] = useState<IssueFilter | null>(null);
  const [salesManagerId, setSalesManagerId] = useState<string | undefined>();
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);

  // Sort state
  const [sortBy, setSortBy] = useState('health_score');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const dashboardFilters = useMemo(
    () => ({
      salesManagerId: canViewAllAms ? salesManagerId : undefined,
      search: debouncedSearch || undefined,
      status,
      tier,
      sortBy,
      sortDirection,
      pageNumber: page,
      pageLength: pageSize,
      issueFilter: issueFilter ?? undefined,
    }),
    [
      debouncedSearch,
      canViewAllAms,
      issueFilter,
      page,
      pageSize,
      salesManagerId,
      sortBy,
      sortDirection,
      status,
      tier,
    ],
  );

  const { data, isLoading, isError, isFetching } = useAmDashboard(dashboardFilters);
  const accountNamesQuery = useAmDashboardAccountNames();

  const accountOptions = useMemo(() => {
    const seen = new Set<string>();
    return (accountNamesQuery.data || []).reduce<{ label: string; value: string }[]>(
      (options, account) => {
        const name = account.name?.trim();
        if (!name || seen.has(name)) return options;
        seen.add(name);
        options.push({ label: name, value: name });
        return options;
      },
      [],
    );
  }, [accountNamesQuery.data]);

  const searchableSelectOptions = useMemo(
    () => ({ name: accountOptions }),
    [accountOptions],
  );

  const summary = data?.summary ?? null;

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleRefresh = () => {
    void queryClient.invalidateQueries({
      queryKey: amDashboardKeys.dashboard(dashboardFilters),
    });
  };

  const loadAllAccounts = async (): Promise<AmDashboardAccount[]> => {
    const allAccounts: AmDashboardAccount[] = [];
    let pageNumber = 0;
    let totalCount: number;

    do {
      const batch = await fetchAmDashboard({
        ...dashboardFilters,
        pageNumber,
        pageLength: 100,
      });

      const batchAccounts = batch.accounts || [];
      allAccounts.push(...batchAccounts);
      totalCount = batch.pagination.total;
      pageNumber += 1;

      if (batchAccounts.length === 0) break;
    } while (allAccounts.length < totalCount);

    return allAccounts;
  };

  function handleSortChange(column: string) {
    if (sortBy === column) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortBy(column);
      setSortDirection(1);
    }
    setPage(0);
  }

  function handleIssueFilterChange(filter: IssueFilter | null) {
    setIssueFilter(filter);
    setPage(0);
  }

  const { data: salesManagersData } = useSalesManagers();
  const allManagers = useMemo(
    () => (salesManagersData ?? []).map((m) => ({ id: m.id, name: m.name, isFormer: m.isFormer ?? false })),
    [salesManagersData],
  );

  // Directors/admins with no specific AM selected → summary table of all AMs
  if (canViewAllAms && !salesManagerId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">AM Dashboard</h1>
          <AmFilterDropdown value={salesManagerId} onChange={setSalesManagerId} />
        </div>
        <AmSummaryTable ams={allManagers} onSelectAm={setSalesManagerId} />
      </div>
    );
  }

  // Non-admin/director users (or director with a specific AM selected): existing layout
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AM Dashboard</h1>
          {data?.lastComputedAt && (
            <p className="text-xs text-muted-foreground">
              Latest snapshot updated:{' '}
              {new Date(data.lastComputedAt).toLocaleString('en-CA', {
                timeZone: 'America/Montreal',
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          )}
        </div>
        {canViewAllAms && (
          <AmFilterDropdown value={salesManagerId} onChange={setSalesManagerId} />
        )}
      </div>

      {/* Activity Summary */}
      {summary && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-slate-700">Page Summary</h2>
            <MetricTooltip
              label="Explain page summary totals"
              text={PAGE_SUMMARY_TOOLTIP}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <SummaryCard
              title="Active Transmitters"
              tooltipText={SUMMARY_CARD_TOOLTIPS.activeTransmitters}
              count={summary.activeTransmitters.count}
              accountsAffected={summary.activeTransmitters.accountsAffected}
              windowHours={2}
              accentBorderClassName="border-l-green-500"
              valueClassName="text-green-600"
            />
            <SummaryCard
              title="Alerts Opened"
              tooltipText={SUMMARY_CARD_TOOLTIPS.alertsOpened}
              count={summary.alertsOpened.count}
              accountsAffected={summary.alertsOpened.accountsAffected}
              windowHours={HISTORY_WINDOW_HOURS}
              accentBorderClassName="border-l-amber-500"
              valueClassName="text-amber-600"
            />
            <SummaryCard
              title="Emails Sent"
              tooltipText={SUMMARY_CARD_TOOLTIPS.emailsSent}
              count={summary.emailsSent.count}
              accountsAffected={summary.emailsSent.accountsAffected}
              windowHours={HISTORY_WINDOW_HOURS}
              accentBorderClassName="border-l-sky-500"
              valueClassName="text-sky-600"
            />
            <SummaryCard
              title="Calls Sent"
              tooltipText={SUMMARY_CARD_TOOLTIPS.callsSent}
              count={summary.callsSent.count}
              accountsAffected={summary.callsSent.accountsAffected}
              windowHours={HISTORY_WINDOW_HOURS}
              accentBorderClassName="border-l-indigo-500"
              valueClassName="text-indigo-600"
            />
            <SummaryCard
              title="Expiring Contracts"
              tooltipText={SUMMARY_CARD_TOOLTIPS.expiringContracts}
              count={summary.expiringContracts.count}
              accountsAffected={summary.expiringContracts.accountsAffected}
              helperText={`Across ${summary.expiringContracts.accountsAffected.toLocaleString()} account${
                summary.expiringContracts.accountsAffected !== 1 ? 's' : ''
              } expiring within 90 days`}
              windowHours={HISTORY_WINDOW_HOURS}
              accentBorderClassName="border-l-blue-500"
              valueClassName="text-blue-600"
            />
          </div>
        </div>
      )}

      {/* Urgent Strip */}
      {summary && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-slate-700">Urgent Issues</h2>
            <MetricTooltip
              label="Explain urgent issue totals"
              text={URGENT_ISSUES_TOOLTIP}
            />
          </div>
          <UrgentStrip
            summary={summary}
            activeFilter={issueFilter}
            onFilterChange={handleIssueFilterChange}
          />
        </div>
      )}

      {/* Filter banner */}
      {issueFilter && (
        <div className="flex items-center gap-2 rounded-md bg-yellow-50 px-4 py-2 text-sm text-yellow-800">
          Showing accounts with{' '}
          <strong>
            {issueFilter
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase())}
          </strong>
          {' '}&mdash;{' '}
          <button
            className="text-blue-600 underline"
            onClick={() => handleIssueFilterChange(null)}
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Table toolbar */}
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search accounts..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          className="w-[250px]"
        />
        <Select
          value={status ?? 'all'}
          onValueChange={(v) => {
            setStatus(v === 'all' ? undefined : v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="churned">Churned</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="prospect">Prospect</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={tier ?? 'all'}
          onValueChange={(v) => {
            setTier(v === 'all' ? undefined : v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3">
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v));
              setPage(0);
            }}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 / page</SelectItem>
              <SelectItem value="50">50 / page</SelectItem>
              <SelectItem value="100">100 / page</SelectItem>
            </SelectContent>
          </Select>
          {data?.pagination && (
            <span className="text-xs text-muted-foreground">
              {data.pagination.total} accounts
            </span>
          )}
        </div>
      </div>

      {/* Portfolio Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          Loading...
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20 text-destructive">
          Failed to load dashboard data. Please try again later.
        </div>
      ) : data ? (
        <AccountPortfolioTable
          accounts={data.accounts}
          pagination={data.pagination}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onPageChange={setPage}
          columnFilters={columnFilters}
          onColumnFilterChange={setColumnFilter}
          onClearColumnFilters={() => setColumnFilters({})}
          onRefresh={handleRefresh}
          isRefreshing={isFetching}
          loadAllAccounts={loadAllAccounts}
          searchableSelectOptions={searchableSelectOptions}
        />
      ) : null}
    </div>
  );
}
